<template>
  <div>
    <div v-if="motion" class="p-p-3">
      <header class="p-d-flex">
        <div class="flex-grow-1">
          <h1>{{motion.name}}</h1>
          <motion-state-display :state="motion.state" :reason="motion.stateReason"/>
        </div>
        <div class="p-pt-5 p-pr-5">
          <button class="pure-button button-success" @click="carried">Carried</button>
          <button class="pure-button button-error" @click="lost">Lost</button>
          <button type="button" class="pure-button" @click="toggleActions">More</button>
          <p-menu ref="actionsMenu" :model="actions" :popup="true" />
        </div>
      </header>

      <div v-if="motion.speaking()">
        Now speaking: {{motion.speaking().delegate.getName()}} ({{motion.speaking().type}})
      </div>

      <div class="p-d-flex">
        <div class="flex-grow-1">
          <h4>Proposer</h4>
          <div class="p-d-flex p-ai-center">
            <div>
              <speaking-indicator v-show="proposerIsSpeaking"/>
              <spoken-indicator v-show="proposerHasSpoken" />
            </div>
            <div class="flex-grow-1">
              <span v-if="motion.proposer">{{motion.proposer.getName()}}</span><br/>
              <span v-if="motion.proposer">{{motion.proposer.associationName}}</span>
            </div>
            <div v-if="motion.proposer">
              <button v-if="!proposerHasSpoken && !proposerIsSpeaking" @click="proposerSpeak" class="pure-button button-success" :disabled="seconderIsSpeaking">Speak</button>
              <button v-if="proposerIsSpeaking" @click="proposerFinish" class="pure-button button-warning">Finish</button>
            </div>
          </div>
        </div>

        <div class="flex-grow-1">
          <h4>Seconder</h4>
          <div class="p-d-flex p-ai-center">
            <div>
              <speaking-indicator v-show="seconderIsSpeaking"/>
              <spoken-indicator v-show="seconderHasSpoken" />
            </div>
            <div class="flex-grow-1">
              <span v-if="motion.seconder">{{motion.seconder.getName()}}</span>
            </div>
            <div v-if="motion.seconder">
              <button v-if="!seconderHasSpoken && !seconderIsSpeaking" @click="seconderSpeak" class="pure-button button-success" :disabled="proposerIsSpeaking">Speak</button>
              <button v-if="seconderIsSpeaking" @click="seconderFinish" class="pure-button button-warning">Finish</button>
            </div>
          </div>
        </div>
      </div>
      <div class="p-grid">
        <div class="p-col">
          <h4>For</h4>
          <current-motion-speakers
            :speakers="motion.for"
            :motion="motion"
            @speak="speak"
            @spoken="finish"
            @withdrawn="withdraw"
          />
        </div>
        <div class="p-col">
          <h4>Against</h4>
          <current-motion-speakers
            :speakers="motion.against"
            :motion="motion"
            @speak="speak"
            @spoken="finish"
            @withdrawn="withdraw"
          />
        </div>
      </div>
    </div>
    <div v-if="!motion" v-show="!loadingCurrentMotion">
      <div class="p-d-flex empty-state p-flex-column p-ai-center p-jc-center">
        <svg enable-background="new 0 0 64 64" height="64px" id="Layer_1" version="1.1" viewBox="0 0 64 64" width="64px" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g><path d="M28.813,9.684h6.373c0.44,0,0.797-0.447,0.797-1V6.119c0-1.105-0.714-1.444-1.594-1.444H29.61   c-0.88,0-1.594,0.34-1.594,1.444v2.564C28.017,9.236,28.373,9.684,28.813,9.684z"/><path d="M37.983,7.777v0.906c0,1.654-1.255,3-2.797,3h-6.373c-1.542,0-2.797-1.346-2.797-3V7.777H12v1v35.396c0,2.209,1.791,4,4,4   h10.911l-7.523,6.865c-0.816,0.745-0.874,2.01-0.129,2.826c0.395,0.432,0.936,0.651,1.478,0.651c0.481,0,0.964-0.173,1.348-0.522   L30,50.77v6.555c0,1.104,0.896,2,2,2s2-0.896,2-2v-6.873l8.22,7.543c0.818,0.745,2.083,0.684,2.825-0.134   c0.743-0.816,0.684-2.082-0.134-2.825l-7.485-6.862H48c2.209,0,4-1.791,4-4V8.777v-1H37.983z M48,44.174H16V17.681h32V44.174z"/></g></svg>
        <br/>
        No current motion
      </div>
    </div>
  </div>
</template>

<script>
import HasCurrentMotion from '../../mixins/has-current-motion'
import SpeakingIndicator from '../display/SpeakingIndicator.vue'
import SpokenIndicator from '../display/SpokenIndicator.vue'
import MotionState from '../../enums/motion-state'
import MotionStateReason from '../../enums/motion-state-reason'
import SpeakerState from '../../enums/speaker-state'
import PropType from '../../enums/prop-type'
import CurrentMotionSpeakers from './CurrentMotionSpeakers.vue'
import MotionStateDisplay from '../motions/MotionStateDisplay.vue'
import Menu from 'primevue/menu'
import Button from 'primevue/button'

export default {
  name: 'CurrentMotionControlPanel',
  mixins: [
    HasCurrentMotion
  ],
  components: {
    CurrentMotionSpeakers,
    SpeakingIndicator,
    SpokenIndicator,
    MotionStateDisplay,
    'p-menu': Menu,
    'p-button': Button
  },
  data: function () {
    return {
      showActions: false,
      actions: [
        {
          label: 'Withdrawn',
          command: () => {
            this.changeState(MotionState.PASSED, MotionStateReason.WITHDRAWN)
          }
        },
        {
          label: 'Unfinished',
          command: () => {
            this.changeState(MotionState.HOLD, MotionStateReason.UNFINISHED)
          }
        },
        {
          label: 'Moved to Main',
          command: () => {
            this.changeState(MotionState.PASSED, MotionStateReason.MOVED_TO_MAIN)
          }
        },
        {
          label: 'Div. Carried',
          command: () => {
            this.changeState(MotionState.PASSED, MotionStateReason.DIVISION_CARRIED)
          }
        },
        {
          label: 'Div. Lost',
          command: () => {
            this.changeState(MotionState.FAILED, MotionStateReason.DIVISION_LOST)
          }
        },
        {
          label: 'Hold',
          command: () => {
            this.changeState(MotionState.HOLD)
          }
        },
        {
          label: 'Stop',
          command: () => {
            this.changeState(MotionState.PENDING)
          }
        }
      ]
    }
  },
  computed: {
    proposerIsSpeaking () {
      return this.motion && this.motion.proposerState === SpeakerState.ACTIVE
    },
    proposerHasSpoken () {
      return this.motion && this.motion.proposerState === SpeakerState.SPOKEN
    },
    seconderIsSpeaking () {
      return this.motion && this.motion.seconderState === SpeakerState.ACTIVE
    },
    seconderHasSpoken () {
      return this.motion && this.motion.seconderState === SpeakerState.SPOKEN
    }
  },
  methods: {
    async proposerSpeak () {
      await this.$store.dispatch('motions/propStart', PropType.PROPOSER)
    },
    async proposerFinish () {
      await this.$store.dispatch('motions/propFinish', PropType.PROPOSER)
    },
    async seconderSpeak () {
      await this.$store.dispatch('motions/propStart', PropType.SECONDER)
    },
    async seconderFinish () {
      await this.$store.dispatch('motions/propFinish', PropType.SECONDER)
    },
    async speak (speaker) {
      await this.$store.dispatch('motions/speakerStart', speaker)
    },
    async finish (speaker) {
      await this.$store.dispatch('motions/speakerFinish', speaker)
    },
    async withdraw (speaker) {
      console.log('withdrawing, caught event')
      await this.$store.dispatch('motions/speakerWithdraw', speaker)
    },
    async carried () {
      await this.$store.dispatch('motions/changeState', {
        status: MotionState.PASSED,
        reason: MotionStateReason.CARRIED
      })
    },
    async lost () {
      await this.$store.dispatch('motions/changeState', {
        status: MotionState.FAILED,
        reason: MotionStateReason.LOST
      })
    },
    async changeState (status, reason = null) {
      await this.$store.dispatch('motions/changeState', {
        status: status,
        reason: reason
      })
    },
    toggleActions (event) {
      console.log('toggle actions')
      console.log(this.$refs.actionsMenu)
      this.$refs.actionsMenu.toggle(event)
    }
  }
}
</script>

<style scoped>
.spoken-indicator {
  width: 60px;
  text-align: center;
}
.spoken-indicator svg {
  width: 30px;
}
.empty-state {
  min-height: 100vh;
}
.empty-state svg {
  width: 150px;
  height: auto;
  fill: #eee;
}
</style>



// WEBPACK FOOTER //
// src/components/control/CurrentMotionControlPanel.vue